import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { KeyRound, Loader2, CheckCircle2, Eye, EyeOff, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.new_password });
      setDone(true);
      toast.success("Password updated — sign in with the new one");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-yellow p-4">
      <div className="brutal bg-white max-w-md w-full p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
            <Zap className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none">ReferPro</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">Set a new password</div>
          </div>
        </div>

        {!token ? (
          <div className="text-center" data-testid="reset-no-token">
            <div className="w-14 h-14 bg-red-200 border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4 mx-auto">
              <AlertTriangle className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <h2 className="font-display font-black text-2xl">Link looks broken</h2>
            <p className="text-sm text-neutral-700 mt-2">
              We couldn't find a reset token in this link. Request a fresh one to try again.
            </p>
            <Link to="/forgot-password" className="btn-brutal btn-primary w-full mt-6">
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="w-14 h-14 bg-brand-green border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4 mx-auto">
              <CheckCircle2 className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <h2 className="font-display font-black text-2xl">All set!</h2>
            <p className="text-sm text-neutral-700 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Choose a new password.</h1>
            <p className="text-neutral-600 mt-2 text-sm">Use at least 8 characters. Make it memorable but hard to guess.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">New password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={form.new_password}
                    onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                    className="input-brutal pr-12"
                    minLength={8}
                    required
                    autoFocus
                    data-testid="reset-new"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black"
                    aria-label="Toggle visibility"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Confirm</label>
                <input
                  type={show ? "text" : "password"}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="input-brutal"
                  required
                  data-testid="reset-confirm"
                />
              </div>
              <button type="submit" disabled={busy} className="btn-brutal btn-primary w-full py-3" data-testid="reset-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>

            <Link
              to="/login"
              className="block text-center text-xs font-bold underline text-neutral-700 hover:text-black mt-4"
            >
              ← Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
